import React, { createContext, useContext, useEffect, useState } from "react";

const translations = {
  en: {
    nav: {
      fleet: "Fleet",
      about: "About",
      contact: "Contact",
      book: "Book a car",
    },
    common: {
      perDay: "/ day",
      from: "From",
      currency: "USD",
      bookNow: "Book now",
      viewDetails: "View details",
      total: "Total",
      days: "days",
      day: "day",
      learnMore: "Learn more",
      backToFleet: "Back to fleet",
      sending: "Sending…",
      processing: "Processing…",
    },
    hero: {
      eyebrow: "Everett · Greater Boston",
      title1: "From $28.99/day.",
      title2: "Airport delivery. Unlimited miles.",
      sub: "Popular Toyotas, Hondas, Nissans and Fords — picked up at Logan Airport or our Everett lot. Insurance included, no mileage cap.",
      ctaPrimary: "Reserve your car",
      ctaSecondary: "See the fleet",
    },
    fleet: {
      eyebrow: "The Fleet",
      title: "Everyday cars priced for everyday people.",
      filters: {
        all: "All",
        economy: "Economy",
        compact: "Compact",
        midsize: "Midsize",
        suv: "SUV",
        minivan: "Minivan",
        van: "Van",
      },
    },
    car: {
      transmission: "Transmission",
      fuel: "Fuel",
      seats: "Seats",
      doors: "Doors",
      year: "Year",
      includes: "What's included",
      bookThis: "Book this car",
    },
    booking: {
      title: "Reserve your drive",
      eyebrow: "Reservation",
      fullName: "Full name",
      email: "Email",
      phone: "Phone",
      pickupDate: "Pick-up date",
      returnDate: "Return date",
      pickupLocation: "Pick-up location",
      pickupLocationPlaceholder: "Logan Airport · Back Bay · Hotel address",
      airportPickup: "Pick me up at Logan Airport (BOS)",
      airportPickupHint: "We meet you at terminal arrivals — usually within 15 minutes of landing.",
      flightNumber: "Flight number",
      flightNumberPlaceholder: "e.g. AA 1234 · JetBlue B6 2025",
      notes: "Notes for our team (optional)",
      notesPlaceholder: "Special requests, child seats, delivery instructions…",
      summary: "Summary",
      payOnline: "Pay now with Stripe",
      requestOnly: "Send reservation request",
      payDescription: "Secure payment via Stripe. Booking confirmed instantly.",
      requestDescription: "Our concierge will call you within 1 hour to confirm.",
      success: "Reservation request received. Check your inbox shortly.",
      missingFields: "Please complete all required fields.",
      datesInvalid: "Return date must be after pick-up date.",
    },
    success: {
      title: "Your drive is confirmed.",
      sub: "Thank you for booking with Movix. A confirmation email is on its way with your pickup details.",
      checking: "Confirming your payment…",
      failed: "Payment was not completed. Please try again.",
      expired: "Your payment session expired. Please start a new booking.",
      cta: "Back to fleet",
    },
    contact: {
      eyebrow: "Get in touch",
      title: "We answer in under 30 minutes.",
      sub: "Whether you need a last-minute Logan pickup or a week-long Cape Cod adventure, our concierge is on standby.",
      name: "Name",
      email: "Email",
      phone: "Phone (optional)",
      subject: "Subject",
      message: "How can we help?",
      send: "Send message",
      sent: "Message sent. We will be in touch shortly.",
      address: "70 Chelsea St, Everett, MA 02149",
      phoneLabel: "(561) 455-6277",
      hours: "Mon–Sun · 7:00 — 22:00",
    },
    about: {
      eyebrow: "About Movix",
      title: "Honest car rental for Greater Boston.",
      p1: "Movix Car Rental is an Everett-based, family-run operation built for travelers, students and locals who just need a dependable car at a fair price. Every vehicle is cleaned, inspected and topped-up before each handover.",
      p2: "We pick you up at Logan, drop the car at your hotel, or hand over the keys at our lot on 70 Chelsea St. Insurance, unlimited mileage and 24/7 roadside are always included.",
      stats: [
        { v: "30+", l: "Cars available" },
        { v: "5k+", l: "Trips delivered" },
        { v: "4.9", l: "Google rating" },
        { v: "24/7", l: "Customer support" },
      ],
    },
    footer: {
      tagline: "Boston's boutique rental for cinematic drives.",
      explore: "Explore",
      company: "Company",
      contact: "Contact",
      rights: "All rights reserved.",
    },
    lang: { en: "EN", pt: "PT" },
  },
  pt: {
    nav: {
      fleet: "Frota",
      about: "Sobre",
      contact: "Contato",
      book: "Reservar",
    },
    common: {
      perDay: "/ diária",
      from: "A partir de",
      currency: "USD",
      bookNow: "Reservar",
      viewDetails: "Ver detalhes",
      total: "Total",
      days: "dias",
      day: "dia",
      learnMore: "Saiba mais",
      backToFleet: "Voltar à frota",
      sending: "Enviando…",
      processing: "Processando…",
    },
    hero: {
      eyebrow: "Everett · Grande Boston",
      title1: "A partir de $28,99/dia.",
      title2: "Entrega no aeroporto. Milhas livres.",
      sub: "Toyotas, Hondas, Nissans e Fords populares — buscamos você no Aeroporto Logan ou retire na nossa loja em Everett. Seguro incluso, sem limite de quilometragem.",
      ctaPrimary: "Reservar carro",
      ctaSecondary: "Ver a frota",
    },
    fleet: {
      eyebrow: "A Frota",
      title: "Carros do dia a dia com preço de gente normal.",
      filters: {
        all: "Todos",
        economy: "Econômico",
        compact: "Compacto",
        midsize: "Sedan médio",
        suv: "SUV",
        minivan: "Minivan",
        van: "Van",
      },
    },
    car: {
      transmission: "Câmbio",
      fuel: "Combustível",
      seats: "Lugares",
      doors: "Portas",
      year: "Ano",
      includes: "O que está incluído",
      bookThis: "Reservar este carro",
    },
    booking: {
      title: "Reserve sua experiência",
      eyebrow: "Reserva",
      fullName: "Nome completo",
      email: "E-mail",
      phone: "Telefone",
      pickupDate: "Data de retirada",
      returnDate: "Data de devolução",
      pickupLocation: "Local de retirada",
      pickupLocationPlaceholder:
        "Aeroporto Logan · Back Bay · Endereço do hotel",
      airportPickup: "Buscar no Aeroporto Logan (BOS)",
      airportPickupHint:
        "A gente te encontra na saída do terminal — normalmente em até 15 minutos do pouso.",
      flightNumber: "Número do voo",
      flightNumberPlaceholder: "ex.: AA 1234 · LATAM LA 8181",
      notes: "Notas para o time (opcional)",
      notesPlaceholder:
        "Pedidos especiais, cadeirinha, instruções de entrega…",
      summary: "Resumo",
      payOnline: "Pagar agora com Stripe",
      requestOnly: "Enviar solicitação de reserva",
      payDescription:
        "Pagamento seguro via Stripe. Reserva confirmada instantaneamente.",
      requestDescription:
        "Nosso concierge liga em até 1 hora para confirmar.",
      success: "Solicitação recebida. Em instantes você recebe um e-mail.",
      missingFields: "Preencha todos os campos obrigatórios.",
      datesInvalid: "A devolução deve ser depois da retirada.",
    },
    success: {
      title: "Sua reserva está confirmada.",
      sub: "Obrigado por reservar com a Movix. Um e-mail de confirmação está a caminho com os detalhes da retirada.",
      checking: "Confirmando seu pagamento…",
      failed: "O pagamento não foi concluído. Tente novamente.",
      expired: "Sua sessão de pagamento expirou. Inicie uma nova reserva.",
      cta: "Voltar à frota",
    },
    contact: {
      eyebrow: "Fale conosco",
      title: "Respondemos em menos de 30 minutos.",
      sub: "De uma retirada de última hora no Logan a um road trip em Cape Cod, nosso concierge está de plantão.",
      name: "Nome",
      email: "E-mail",
      phone: "Telefone (opcional)",
      subject: "Assunto",
      message: "Como podemos ajudar?",
      send: "Enviar mensagem",
      sent: "Mensagem enviada. Entraremos em contato em breve.",
      address: "70 Chelsea St, Everett, MA 02149",
      phoneLabel: "(561) 455-6277",
      hours: "Seg–Dom · 7h00 — 22h00",
    },
    about: {
      eyebrow: "Sobre a Movix",
      title: "Locadora honesta para a Grande Boston.",
      p1: "A Movix Car Rental é uma operação familiar, baseada em Everett, feita para viajantes, estudantes e moradores que precisam de um carro confiável com preço justo. Todo carro é lavado, revisado e abastecido antes de cada entrega.",
      p2: "Buscamos você no Logan, entregamos no seu hotel ou deixamos a chave na nossa loja na 70 Chelsea St. Seguro, quilometragem livre e assistência 24/7 inclusos sempre.",
      stats: [
        { v: "30+", l: "Carros disponíveis" },
        { v: "5k+", l: "Locações entregues" },
        { v: "4,9", l: "Nota no Google" },
        { v: "24/7", l: "Atendimento" },
      ],
    },
    footer: {
      tagline: "A locadora boutique de Boston para drives cinematográficos.",
      explore: "Explorar",
      company: "Empresa",
      contact: "Contato",
      rights: "Todos os direitos reservados.",
    },
    lang: { en: "EN", pt: "PT" },
  },
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("movix_lang") || "en";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("movix_lang", lang);
    }
  }, [lang]);

  const t = translations[lang];

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};

export const formatPrice = (value, currency = "USD") => {
  const hasCents = Number(value) % 1 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
};
